import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Button from '../../library/button/button';
import Checkbox from '../../library/checkbox/checkbox';
import TreadwellCheckbox from './components/treadwell-checkbox/treadwell-checkbox';
import TreadwellCustomDriverType from './components/treadwell-custom-driver-type/treadwell-custom-driver-type';
import TreadwellDriverTypeOptions from './components/treadwell-driver-type-options/treadwell-driver-type-options';

import {
  useAemContent,
  useFitment,
  useTreadwellPersonas
} from '../../shared/hooks/hooks';

import {
  getEventUIType,
  trackAction
} from '../../shared/utils/analytics/analytics';
import { isArrayWithLength } from '../../shared/utils/validators/validators';

import {
  AbstractCmsComponent,
  CmsToolTipsComponent,
  Maybe
} from '../../shared/types/graphql-types';
import { AEM_CONTENT_IDS } from '../../shared/constants/cms';
import { TREADWELL_DRIVERTYPE } from '../../shared/constants/data-layer';

import './treadwell-driver-types.scss';

interface TreadwellDriverTypesProps {
  className?: string;
  disableSubmit?: boolean;
  hideBackButton?: boolean;
  hideSubmitButton?: boolean;
  initializeSelectedDrivingType: () => void;
  isCustomDriverTypeShowing?: boolean;
  onDriverTypeSelected: (driverType: CmsToolTipsComponent | null) => void;
  onGoBack?: () => void;
  onPrimaryCTAClick?: () => void;
  onSelectedCustomDrivingPriorities: (
    prioritesList: Array<Maybe<AbstractCmsComponent>>
  ) => void;
  onToggleIsCustomDriverTypeShowing: () => void;
  onUpdateShowInStockTires: () => void;
  onUpdateShowOffRoadTires: () => void;
  onUpdateShowWinterTires: () => void;
  primaryCTAText?: string;
  selectedCustomDrivingPriorities: string[];
  selectedCustomDrivingPrioritiesType: string;
  selectedDriverType?: {
    name: string;
  };
  showInStockTires: boolean;
  showOffRoadTires: boolean;
  showWinterTires: boolean;
}

function TreadwellDriverTypes(props: TreadwellDriverTypesProps) {
  const {
    className,
    disableSubmit = false,
    hideBackButton = false,
    hideSubmitButton = false,
    initializeSelectedDrivingType,
    isCustomDriverTypeShowing,
    onDriverTypeSelected,
    onGoBack,
    onPrimaryCTAClick,
    onSelectedCustomDrivingPriorities,
    onToggleIsCustomDriverTypeShowing,
    onUpdateShowInStockTires,
    onUpdateShowOffRoadTires,
    onUpdateShowWinterTires,
    primaryCTAText = 'Show Results',
    selectedCustomDrivingPriorities,
    selectedCustomDrivingPrioritiesType,
    selectedDriverType,
    showInStockTires,
    showOffRoadTires,
    showWinterTires
  } = props;
  const [driverPersonas, setDriverPersonas] = useState<
    Array<CmsToolTipsComponent> | undefined
  >(undefined);
  const hasInitializedSelectedDrivingTypeRef = useRef(false);
  const location = useLocation();
  const uiElementType = getEventUIType(location);
  const inStockCheckboxLabel = 'Only show in-stock tires';
  const { selectedFitment } = useFitment();
  const { getTreadwellPersonaData } = useTreadwellPersonas();
  const { aemContent } = useAemContent(AEM_CONTENT_IDS.treadwellPersonas);
  const offRoadTiresCheckbox = aemContent?.[AEM_CONTENT_IDS.offroadTires];
  const winterTiresCheckbox = aemContent?.[AEM_CONTENT_IDS.winterTires];
  const showOffRoadFilter =
    !disableSubmit &&
    selectedFitment?.vehicleCategory?.toLowerCase().includes('truck');
  let showOffRoadFilterAnalyticsDriverType: string | undefined = undefined;

  if (showOffRoadFilter && (isCustomDriverTypeShowing || selectedDriverType)) {
    if (isCustomDriverTypeShowing) {
      showOffRoadFilterAnalyticsDriverType =
        selectedCustomDrivingPrioritiesType;
    } else {
      showOffRoadFilterAnalyticsDriverType = selectedDriverType?.name;
    }
  }

  useEffect(() => {
    getTreadwellPersonaData(selectedFitment).then(
      ({ personasList, prioritiesList }) => {
        setDriverPersonas(personasList);

        if (
          !isArrayWithLength(selectedCustomDrivingPriorities) &&
          isArrayWithLength(prioritiesList)
        ) {
          onSelectedCustomDrivingPriorities(prioritiesList);
        }
      }
    );
  }, [
    getTreadwellPersonaData,
    onSelectedCustomDrivingPriorities,
    selectedCustomDrivingPriorities,
    selectedFitment
  ]);

  useEffect(() => {
    if (
      driverPersonas &&
      selectedDriverType?.name &&
      !driverPersonas?.some(
        persona => persona?.name === selectedDriverType?.name
      )
    ) {
      onDriverTypeSelected(null);
    }
  }, [driverPersonas, onDriverTypeSelected, selectedDriverType?.name]);

  useEffect(() => {
    if (showOffRoadFilterAnalyticsDriverType) {
      trackAction('treadwell_drivertype_offroad_show', {
        [TREADWELL_DRIVERTYPE]: showOffRoadFilterAnalyticsDriverType
      });
    }
  }, [showOffRoadFilterAnalyticsDriverType]);

  useEffect(() => {
    if (!hasInitializedSelectedDrivingTypeRef.current) {
      initializeSelectedDrivingType?.();
      hasInitializedSelectedDrivingTypeRef.current = true;
    }
  }, [initializeSelectedDrivingType]);

  return (
    <div className={className} styleName="driver-types">
      {!isCustomDriverTypeShowing && (
        <>
          <TreadwellDriverTypeOptions
            driverPersonas={driverPersonas}
            onDriverTypeSelected={onDriverTypeSelected}
            selectedDriverType={selectedDriverType}
            styleName="driver-type-options"
          />
          <Checkbox
            id="only-in-stock-tires"
            isChecked={showInStockTires}
            label={inStockCheckboxLabel}
            onClick={onUpdateShowInStockTires}
            styleName="only-in-stock-tires"
          />

          <div styleName="hr-wrapper">
            <hr />
          </div>

          {!disableSubmit && (
            <div styleName="treadwell-checkbox-flex-wrapper">
              <div styleName="treadwell-checkbox-container">
                {Boolean(winterTiresCheckbox) && (
                  <TreadwellCheckbox
                    checkboxItems={winterTiresCheckbox}
                    isChecked={showWinterTires}
                    onClick={onUpdateShowWinterTires}
                  />
                )}

                {showOffRoadFilter && Boolean(offRoadTiresCheckbox) && (
                  <TreadwellCheckbox
                    checkboxItems={offRoadTiresCheckbox}
                    isChecked={showOffRoadTires}
                    onClick={onUpdateShowOffRoadTires}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}

      {!hideSubmitButton && (
        <>
          <div styleName="cta-wrapper">
            {!hideBackButton && (
              <Button isSecondary onClick={onGoBack} styleName="go-back-button">
                Go Back
              </Button>
            )}

            {isCustomDriverTypeShowing && (
              <TreadwellCustomDriverType
                className={className}
                onSelectedPriorities={onSelectedCustomDrivingPriorities}
                onToggleIsCustomDriverTypeShowing={
                  onToggleIsCustomDriverTypeShowing
                }
                selectedPriorities={selectedCustomDrivingPriorities}
              />
            )}

            {isCustomDriverTypeShowing && (
              <>
                <div styleName="customize-driver-in-stock-checkbox-container">
                  <Checkbox
                    id="only-in-stock-tires"
                    isChecked={showInStockTires}
                    label={inStockCheckboxLabel}
                    onClick={onUpdateShowInStockTires}
                    styleName="only-in-stock-tires"
                  />
                </div>

                <div styleName="hr-wrapper">
                  <hr />
                </div>

                {!disableSubmit && (
                  <div styleName="treadwell-checkbox-flex-wrapper">
                    <div styleName="treadwell-checkbox-container">
                      {Boolean(winterTiresCheckbox) && (
                        <TreadwellCheckbox
                          checkboxItems={winterTiresCheckbox}
                          isChecked={showWinterTires}
                          onClick={onUpdateShowWinterTires}
                        />
                      )}

                      {showOffRoadFilter && Boolean(offRoadTiresCheckbox) && (
                        <TreadwellCheckbox
                          checkboxItems={offRoadTiresCheckbox}
                          isChecked={showOffRoadTires}
                          onClick={onUpdateShowOffRoadTires}
                        />
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            <div styleName="customize-results-btns-container">
              <Button
                data-testid="treadwell-driver-types__show-results"
                isDisabled={disableSubmit}
                isPrimary
                onClick={onPrimaryCTAClick}
                styleName="submit-button"
              >
                {primaryCTAText}
              </Button>

              {!isCustomDriverTypeShowing && (
                <Button
                  isSecondary
                  onClick={() => {
                    trackAction(
                      `shop_${uiElementType}_treadwell_customize_drivertype`
                    );
                    onToggleIsCustomDriverTypeShowing();
                  }}
                  styleName="customize-driver-button"
                >
                  Customize Driver Type
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TreadwellDriverTypes;
